/**
 * The run record: the canonical state of a run. Expedition number, seed and the ordered actions
 * are enough to replay everything. See spec/formats.md, "RunRecord".
 */
import { dailySeed } from "./daily.ts";
import { WORLD_VERSION } from "./world.ts";

export type Mode = "daily" | "endless";

/** "r" reveal, "f" toggle banner, "c" chord, "o" a member taken out of a party (spec 6.8). */
export type ActionKind = "r" | "f" | "c" | "o";

/**
 * `[kind, x, y]`, or `[kind, x, y, member]` in a party's run when the member is not the first
 * (spec 6.8). A missing member is member 0.
 */
export type Action =
  | readonly [ActionKind, number, number]
  | readonly [ActionKind, number, number, number];

/** The most members a party may have; the core's PARTY_MAX (spec 6.8). */
const PARTY_LIMIT = 6;

export interface RunRecord {
  readonly v: 1;
  /** The rules version the run was played under; see WORLD_VERSION. */
  readonly world: number;
  readonly mode: Mode;
  /** Unsigned 32-bit world seed. */
  readonly seed: number;
  /** Day number for daily runs, null for endless. */
  readonly day: number | null;
  /** Unix milliseconds when the run started. Informational; replay ignores it. */
  readonly startedAt: number;
  /** How many members the party has, 2 to 6; absent for a run played alone (spec 6.8). */
  readonly party?: number;
  readonly actions: readonly Action[];
}

const ACTION_KINDS: ReadonlySet<string> = new Set(["r", "f", "c", "o"]);

function isUint32(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 0xffffffff;
}

/** Validates an untrusted value (for example parsed JSON) as a RunRecord. Throws on any problem. */
export function parseRunRecord(value: unknown): RunRecord {
  if (typeof value !== "object" || value === null) throw new Error("record: not an object");
  const r = value as Record<string, unknown>;
  if (r.v !== 1) throw new Error("record: unsupported version");
  if (r.world !== WORLD_VERSION) throw new Error("record: unsupported world version");
  if (r.mode !== "daily" && r.mode !== "endless") throw new Error("record: bad mode");
  if (!isUint32(r.seed)) throw new Error("record: seed must be an unsigned 32-bit integer");
  if (typeof r.startedAt !== "number" || !Number.isInteger(r.startedAt)) {
    throw new Error("record: startedAt must be an integer");
  }
  let day: number | null = null;
  if (r.mode === "daily") {
    if (typeof r.day !== "number" || !Number.isInteger(r.day)) {
      throw new Error("record: daily runs need an integer day");
    }
    if (dailySeed(r.day) !== r.seed) throw new Error("record: seed does not match the day");
    day = r.day;
  } else if (r.day !== null && r.day !== undefined) {
    throw new Error("record: endless runs have no day");
  }
  let party: number | undefined;
  if (r.party !== undefined) {
    if (typeof r.party !== "number" || !Number.isInteger(r.party)) {
      throw new Error("record: party must be an integer");
    }
    if (r.party < 2 || r.party > PARTY_LIMIT) {
      throw new Error(`record: party must be from 2 to ${PARTY_LIMIT}`);
    }
    party = r.party;
  }
  if (!Array.isArray(r.actions)) throw new Error("record: actions must be an array");
  const actions: Action[] = r.actions.map((a, i) => {
    if (!Array.isArray(a) || (a.length !== 3 && a.length !== 4)) {
      throw new Error(`record: action ${i} malformed`);
    }
    const [kind, x, y, member] = a as unknown[];
    if (typeof kind !== "string" || !ACTION_KINDS.has(kind)) {
      throw new Error(`record: action ${i} has an unknown kind`);
    }
    if (!Number.isInteger(x) || !Number.isInteger(y)) {
      throw new Error(`record: action ${i} has non-integer coordinates`);
    }
    if (a.length === 3) return [kind as ActionKind, x as number, y as number];
    if (!Number.isInteger(member) || (member as number) < 1) {
      throw new Error(`record: action ${i} has a member that is not a positive integer`);
    }
    return [kind as ActionKind, x as number, y as number, member as number];
  });
  return {
    v: 1,
    world: WORLD_VERSION,
    mode: r.mode,
    seed: r.seed,
    day,
    startedAt: r.startedAt,
    ...(party !== undefined ? { party } : {}),
    actions,
  };
}
